// src/microsoftPhoto.js - fetch and remember the signed-in person's Microsoft photo
import { supabase } from './supabaseClient';

const KEY = (userId) => `ms_photo_${userId}`;

/**
 * Why this is cached rather than fetched when needed.
 *
 * Microsoft Graph is the only place the photo exists, and reaching it needs a
 * Microsoft access token. Supabase hands one back as `session.provider_token`,
 * but only on the response to signing in — it is deliberately not kept in the
 * stored session, so on every later page load it is null. Fetching on demand
 * would therefore work exactly once, at sign-in, and never again.
 *
 * So the photo is fetched at the one moment a token exists and kept as a data
 * URL in localStorage. Photos are tens of kilobytes and localStorage holds
 * about five megabytes, so one per admin is not close to a problem.
 *
 * Keyed by user id so that two people sharing a browser do not see each
 * other's face, and cleared on sign-out.
 */

/** Store the photo if this session carries a usable Microsoft token. */
export async function capturePhotoIfPossible(session) {
  const token = session?.provider_token;
  const userId = session?.user?.id;
  if (!token || !userId) return null;

  try {
    const res = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
      headers: { Authorization: `Bearer ${token}` },
    });

    // 404 is the ordinary answer for someone who never set a picture, and 403
    // means the tenant did not grant User.Read. Neither is worth an error in
    // the console; both simply mean there is no photo to show.
    if (!res.ok) {
      if (res.status !== 404) {
        console.warn(`Microsoft photo unavailable (HTTP ${res.status})`);
      }
      return null;
    }

    const blob = await res.blob();
    if (!blob.size) return null;

    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    try {
      localStorage.setItem(KEY(userId), dataUrl);
    } catch (err) {
      // A full or blocked localStorage should cost the photo, nothing else.
      console.warn('Could not cache the Microsoft photo:', err);
    }
    return dataUrl;
  } catch (err) {
    console.warn('Could not fetch the Microsoft photo:', err);
    return null;
  }
}

/** The photo stored for this user, or null. */
export function cachedPhoto(userId) {
  if (!userId) return null;
  try {
    return localStorage.getItem(KEY(userId));
  } catch {
    return null;
  }
}

/** Forget it, so the next person to sign in on this browser does not see it. */
export function clearCachedPhoto(userId) {
  if (!userId) return;
  try {
    localStorage.removeItem(KEY(userId));
  } catch {
    /* nothing to do */
  }
}

/**
 * Catch the token on the way past.
 *
 * The OAuth redirect lands on /dashboard and supabase-js parses it there, so
 * SIGNED_IN fires wherever the app happens to be — not on the login page. This
 * has to be armed app-wide, once, rather than inside the login screen.
 */
export function watchForPhoto() {
  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
      capturePhotoIfPossible(session);
    }
    if (event === 'SIGNED_OUT') {
      // The session is already gone here, so the id is not available to clear
      // one key precisely. Clearing every cached photo is the safe reading of
      // "somebody signed out of this browser".
      try {
        Object.keys(localStorage)
          .filter(k => k.startsWith('ms_photo_'))
          .forEach(k => localStorage.removeItem(k));
      } catch {
        /* nothing to do */
      }
    }
  });
  return () => data?.subscription?.unsubscribe?.();
}
